import React, { useEffect, useState } from 'react'
import rawDataService, { ProjectNode } from '../services/rawDataService'
import DropdownInput, { DropdownOption } from './DropdownInput'

interface CreateRawDataModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: RawDataFormData) => Promise<void>
  uploadMode: 'manual' | 'auto'
  /** 관리자/프로젝트 생성 전역 권한 사용자는 프로젝트명을 직접 입력할 수 있음 */
  canDirectProjectInput?: boolean
}

export interface RawDataFormData {
  projectName: string
  streamGroup: string
  stream: string
  projectDescription?: string
  streamGroupDescription?: string
  streamDescription?: string
  existingProjectId?: number
  uploadMode: 'manual' | 'auto'
}

export default function CreateRawDataModal({ isOpen, onClose, onSubmit, uploadMode, canDirectProjectInput }: CreateRawDataModalProps) {
  const MAX_DESC = 1000
  const sortNodesByName = (nodes: ProjectNode[]) =>
    [...(nodes || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))

  const [formData, setFormData] = useState<RawDataFormData>({
    projectName: '',
    streamGroup: '',
    stream: '',
    projectDescription: '',
    streamGroupDescription: '',
    streamDescription: '',
    uploadMode
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 선택/입력 모드 및 데이터
  const DIRECT_INPUT = '__input__'
  const [projects, setProjects] = useState<ProjectNode[]>([])
  const [projectSelectedNode, setProjectSelectedNode] = useState<ProjectNode | null>(null)

  const [groups, setGroups] = useState<ProjectNode[]>([])
  const [groupSelectedNode, setGroupSelectedNode] = useState<ProjectNode | null>(null)

  const [streams, setStreams] = useState<ProjectNode[]>([])
  const [streamSelectedNode, setStreamSelectedNode] = useState<ProjectNode | null>(null)

  // 초기 프로젝트 목록 로드
  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      try {
        const roots = await rawDataService.getRootNodes(uploadMode)
        // 프로젝트 생성 권한(create)을 가진 프로젝트(루트)만 선택 가능하게 제한
        const filtered = (roots || []).filter((n: any) => {
          const isRoot = n.parent_id == null
          const actions = n.actions as Record<string, boolean> | undefined
          return isRoot && !!actions?.create
        })
        setProjects(sortNodesByName(filtered))
      } catch {
        setProjects([])
      }
    })()
  }, [isOpen, uploadMode])

  useEffect(() => {
    if (!isOpen) return
    setFormData((prev) => ({ ...prev, uploadMode }))
  }, [uploadMode, isOpen])

  // 초기화
  useEffect(() => {
    if (!isOpen) return
    setProjectSelectedNode(null)
    setGroupSelectedNode(null)
    setStreamSelectedNode(null)
  }, [isOpen])


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // 유효성: 1 또는 1-2 또는 1-2-3만 허용
    const effectiveProjectName = (formData.projectName || '').trim()
    const effectiveGroupName = (formData.streamGroup || '').trim()
    const effectiveStreamName = (formData.stream || '').trim()

    if (!effectiveProjectName) {
      alert('프로젝트명(1단계)은 필수입니다.')
      return
    }
    if (effectiveStreamName && !effectiveGroupName) {
      alert('3단계를 입력하려면 2단계를 먼저 입력해야 합니다.')
      return
    }

    try {
      setIsSubmitting(true)
      await onSubmit({
        projectName: effectiveProjectName,
        streamGroup: effectiveGroupName,
        stream: effectiveStreamName,
        // 기존 항목이면 설명 수정 불가 → 새 입력에만 포함
        projectDescription: projectSelectedNode ? undefined : (formData.projectDescription || undefined),
        streamGroupDescription: groupSelectedNode ? undefined : (formData.streamGroupDescription || undefined),
        streamDescription: streamSelectedNode ? undefined : (formData.streamDescription || undefined),
        existingProjectId: projectSelectedNode ? (projectSelectedNode.project_id || projectSelectedNode.id) : undefined,
        uploadMode: formData.uploadMode
      })
      
      // 성공 시 성공 메시지 표시 및 모달 닫기
      alert('✅ 원천데이터가 성공적으로 생성되었습니다!')
      onClose()
      setFormData({ projectName: '', streamGroup: '', stream: '', projectDescription: '', streamGroupDescription: '', streamDescription: '', uploadMode })
      setProjectSelectedNode(null)
      setGroupSelectedNode(null)
      setStreamSelectedNode(null)
      setGroups([])
      setStreams([])
    } catch (error: any) {
      console.error('원천데이터 생성 실패:', error)
      
      // 사용자 친화적 에러 메시지 생성
      let userMessage = '원천데이터 생성에 실패했습니다.'
      
      if (error.message) {
        // 백엔드에서 온 구체적인 에러 메시지 사용
        if (error.message.includes('already exists')) {
          // 중복 에러인 경우 더 친화적으로 변환
          const duplicateStructure = error.message.match(/'([^']+)'/)?.[1]
          if (duplicateStructure) {
            userMessage = `⚠️ 중복된 구조입니다\n\n"${duplicateStructure}" 구조가 이미 존재합니다.\n\n💡 다른 이름을 사용해주세요.`
          } else {
            userMessage = '⚠️ 이미 존재하는 원천데이터 구조입니다.\n\n💡 다른 이름을 사용해주세요.'
          }
        } else if (error.message.includes('입력된 정보')) {
          userMessage = '⚠️ 입력된 정보에 문제가 있습니다.\n\n💡 모든 필드를 올바르게 입력했는지 확인해주세요.'
        } else {
          // 기타 에러 메시지를 그대로 사용하되 이모지 추가
          userMessage = `❌ ${error.message}`
        }
      }
      
      alert(userMessage)
      // 에러 발생 시 모달을 닫지 않음 (사용자가 수정할 수 있도록)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      setFormData({ projectName: '', streamGroup: '', stream: '', projectDescription: '', streamGroupDescription: '', streamDescription: '', uploadMode })
      setProjectSelectedNode(null)
      setGroupSelectedNode(null)
      setStreamSelectedNode(null)
      setGroups([])
      setStreams([])
    }
  }


  if (!isOpen) {
    return null
  }


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">원천 데이터 생성</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
            현재 생성되는 원천 데이터는 <strong>{uploadMode === 'manual' ? '수동 업로드' : '자동 업로드'}</strong> 대상으로 설정됩니다.
          </div>
          {/* 프로젝트 */}
          <div>
            <DropdownInput
              label={"프로젝트명 *"}
              value={formData.projectName}
              onChange={(v) => setFormData(fd => ({ ...fd, projectName: v }))}
              options={projects as DropdownOption[]}
              placeholder={projects.length === 0 ? '생성 권한이 있는 수동 업로드 프로젝트가 없습니다' : '프로젝트를 선택하세요'}
              // 관리자/전역 project_create 권한자는 직접입력 허용, 그 외에는 선택만 가능
              inputDisabled={isSubmitting || !canDirectProjectInput || !!projectSelectedNode}
              dropdownDisabled={isSubmitting || projects.length === 0}
              onPickExisting={async (opt) => {
                const node = projects.find(p => String(p.id) === String(opt.id)) || null
                setProjectSelectedNode(node)
                setFormData(fd => ({ ...fd, projectName: opt.name, projectDescription: (opt.description as any) || '', streamGroup: '', stream: '', streamGroupDescription: '', streamDescription: '' }))
                setGroupSelectedNode(null); setGroups([])
                setStreamSelectedNode(null); setStreams([])
                if (node) { try { setGroups(sortNodesByName(await rawDataService.getNodeChildren(node.id, uploadMode))) } catch { setGroups([]) } }
              }}
              onDirectInput={canDirectProjectInput ? () => {
                setProjectSelectedNode(null)
                setFormData(fd => ({ ...fd, projectName: '', projectDescription: '', streamGroup: '', stream: '', streamGroupDescription: '', streamDescription: '' }))
                setGroups([]); setGroupSelectedNode(null)
                setStreams([]); setStreamSelectedNode(null)
              } : undefined}
            />
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">프로젝트 설명</label>
            <textarea
              value={projectSelectedNode ? ((projectSelectedNode?.description as any) || '') : (formData.projectDescription || '')}
              onChange={(e) => { if (!projectSelectedNode) setFormData({ ...formData, projectDescription: e.target.value }) }}
              className={`w-full px-3 py-2 border rounded-md ${!projectSelectedNode ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent' : 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'}`}
              placeholder="프로젝트에 대한 설명"
              rows={2}
              maxLength={MAX_DESC}
              readOnly={!!projectSelectedNode}
              aria-disabled={!!projectSelectedNode}
              title={projectSelectedNode ? '기존 항목의 설명입니다 (수정 불가)' : undefined}
            />
            {!projectSelectedNode && (
              <div className="mt-1 text-right text-xs text-gray-500">{(formData.projectDescription || '').length}/{MAX_DESC}</div>
            )}
          </div>

          {/* 스트림 그룹 */}
          <div>
            <DropdownInput
              label={"스트림 그룹"}
              value={formData.streamGroup}
              onChange={(v) => setFormData(fd => ({ ...fd, streamGroup: v }))}
              options={groups as unknown as DropdownOption[]}
              placeholder={"예: 트랙터"}
              inputDisabled={isSubmitting || !!groupSelectedNode}
              dropdownDisabled={isSubmitting || !(projectSelectedNode || (formData.projectName||'').trim())}
              onPickExisting={async (opt) => {
                const node = groups.find(g => String(g.id) === String(opt.id)) || null
                setGroupSelectedNode(node)
                setFormData(fd => ({ ...fd, streamGroup: opt.name, streamGroupDescription: (opt.description as any) || '', stream: '', streamDescription: '' }))
                setStreamSelectedNode(null); setStreams([])
                if (node) { try { setStreams(sortNodesByName(await rawDataService.getNodeChildren(node.id, uploadMode))) } catch { setStreams([]) } }
              }}
              onDirectInput={() => {
                setGroupSelectedNode(null)
                setFormData(fd => ({ ...fd, streamGroup: '', streamGroupDescription: '', stream: '', streamDescription: '' }))
                setStreams([]); setStreamSelectedNode(null)
              }}
            />
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">스트림 그룹 설명</label>
            <textarea
              value={groupSelectedNode ? ((groupSelectedNode?.description as any) || '') : (formData.streamGroupDescription || '')}
              onChange={(e) => { if (!groupSelectedNode) setFormData({ ...formData, streamGroupDescription: e.target.value }) }}
              className={`w-full px-3 py-2 border rounded-md ${!groupSelectedNode ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent' : 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'}`}
              placeholder="스트림 그룹에 대한 설명"
              rows={2}
              maxLength={MAX_DESC}
              readOnly={!!groupSelectedNode}
              aria-disabled={!!groupSelectedNode}
              title={groupSelectedNode ? '기존 항목의 설명입니다 (수정 불가)' : undefined}
            />
            {!groupSelectedNode && (
              <div className="mt-1 text-right text-xs text-gray-500">{(formData.streamGroupDescription || '').length}/{MAX_DESC}</div>
            )}
          </div>

          {/* 스트림 */}
          <div>
            <DropdownInput
              label={"스트림"}
              value={formData.stream}
              onChange={(v) => setFormData(fd => ({ ...fd, stream: v }))}
              options={streams as unknown as DropdownOption[]}
              placeholder={"예: 카메라1"}
              inputDisabled={isSubmitting || !!streamSelectedNode}
              dropdownDisabled={isSubmitting || !(groupSelectedNode || (formData.streamGroup||'').trim())}
              onPickExisting={(opt) => {
                const node = streams.find(s => String(s.id) === String(opt.id)) || null
                setStreamSelectedNode(node)
                setFormData(fd => ({ ...fd, stream: opt.name, streamDescription: (opt.description as any) || '' }))
              }}
              onDirectInput={() => {
                setStreamSelectedNode(null)
                setFormData(fd => ({ ...fd, stream: '', streamDescription: '' }))
              }}
            />
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">스트림 설명</label>
            <textarea
              value={streamSelectedNode ? ((streamSelectedNode?.description as any) || '') : (formData.streamDescription || '')}
              onChange={(e) => { if (!streamSelectedNode) setFormData({ ...formData, streamDescription: e.target.value }) }}
              className={`w-full px-3 py-2 border rounded-md ${!streamSelectedNode ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent' : 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'}`}
              placeholder="스트림에 대한 설명"
              rows={2}
              maxLength={MAX_DESC}
              readOnly={!!streamSelectedNode}
              aria-disabled={!!streamSelectedNode}
              title={streamSelectedNode ? '기존 항목의 설명입니다 (수정 불가)' : undefined}
            />
            {!streamSelectedNode && (
              <div className="mt-1 text-right text-xs text-gray-500">{(formData.streamDescription || '').length}/{MAX_DESC}</div>
            )}
          </div>

          {/* 버튼 */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 disabled:opacity-50"
            >
              {isSubmitting ? '생성 중...' : '생성'}
            </button>
          </div>
        </form>

        {/* 안내 메시지 */}
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-700">
            <strong>참고:</strong> 원천데이터는 프로젝트 → 스트림 그룹 → 스트림의 3단계 구조로 생성됩니다.
          </p>
          
        </div>
      </div>
    </div>
  )
}
